import * as XLSX from 'xlsx';
import {
    STATUTS, STATUT_MAP, SOURCES, SOURCE_MAP,
    RESULTATS_ACTION, RESULTAT_MAP, OBJECTIONS, OBJECTION_MAP,
    formatDate, formatDateTime, toInputDate, isWaitingAction
} from '../config/prospects';

// ── Export en vrai classeur Excel (.xlsx) ──

export const exportProspectsToExcel = (prospects, fileName = null) => {
    const wb = XLSX.utils.book_new();

    // 1. Feuille principale : Tableau des prospects
    const dataRows = prospects.map(p => {
        const statutObj = STATUT_MAP[p.statut];
        const statutLabel = statutObj ? `${statutObj.emoji} ${statutObj.label}` : (p.statut || '');
        const sourceLabel = SOURCE_MAP[p.source]?.label || p.source || '';

        // Dernière action & son compte-rendu
        const historyList = Array.isArray(p.historique) ? p.historique : [];
        const sortedHistory = [...historyList].sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
        const latest = sortedHistory[0] || null;

        const resObj = latest?.resultat ? RESULTAT_MAP[latest.resultat] : null;
        const resLabel = resObj ? `${resObj.emoji} ${resObj.label}` : (latest?.resultat || '');

        const derniereNote = latest?.note || '';

        // Compilation de TOUS les comptes-rendus & actions de ce prospect pour analyse
        let tousLesComptesRendus = '';
        if (sortedHistory.length > 0) {
            tousLesComptesRendus = sortedHistory
                .map(h => {
                    const d = h.date ? formatDate(h.date, true) : '';
                    const res = h.resultat ? (RESULTAT_MAP[h.resultat]?.label || h.resultat) : '';
                    const obj = h.objection ? (OBJECTION_MAP[h.objection]?.label || h.objection) : '';
                    const note = (h.note || '').trim().replace(/\r?\n+/g, ' ');
                    const resp = h.responsable ? `[${h.responsable}]` : '';

                    const tags = [];
                    if (res) tags.push(res);
                    if (obj) tags.push(`Frein: ${obj}`);
                    if (resp) tags.push(resp);
                    const tagsStr = tags.length > 0 ? ` (${tags.join(' · ')})` : '';

                    const header = `${d ? d + ' · ' : ''}${h.action || 'Action'}${tagsStr}`;
                    return note ? `• ${header} : ${note}` : `• ${header}`;
                })
                .filter(Boolean)
                .join('\n');
        } else if (p.derniereAction) {
            tousLesComptesRendus = `• ${p.dateDerniereAction ? formatDate(p.dateDerniereAction, true) + ' · ' : ''}${p.derniereAction}`;
        }

        // Prochaine action (messages intelligents)
        let prochaineActionLabel = p.prochaineAction || '';
        if (p.statut === 'gagne') {
            prochaineActionLabel = 'Client converti';
        } else if (p.statut === 'pas_interesse') {
            prochaineActionLabel = 'Sans suite (Refus)';
        }

        // Date prochaine action (messages intelligents)
        let dateProchaineActionLabel = '';
        if (p.statut === 'gagne') {
            dateProchaineActionLabel = 'Dossier clos';
        } else if (p.statut === 'pas_interesse') {
            dateProchaineActionLabel = 'Archivé';
        } else if (!p.dateProchaineAction) {
            if (isWaitingAction(p.prochaineAction)) {
                dateProchaineActionLabel = 'En attente de retour';
            } else {
                dateProchaineActionLabel = 'À planifier';
            }
        } else {
            const formatted = formatDate(p.dateProchaineAction, true);
            dateProchaineActionLabel = p.heureProchaineAction ? `${formatted} à ${p.heureProchaineAction}` : formatted;
        }

        const objObj = p.objection ? OBJECTION_MAP[p.objection] : null;
        const objLabel = objObj ? `${objObj.emoji} ${objObj.label}` : (p.objection || '');

        return {
            'Client / Établissement': p.etablissement || '',
            'Type d\'établissement': p.typeEtablissement || '',
            'Ville': p.ville || '',
            'Contact': p.contact || '',
            'Téléphone': p.telephone || '',
            'Email': p.email || '',
            'Source / Canal': sourceLabel,
            'Statut': statutLabel,
            'Niveau d\'intérêt (%)': p.interet ?? 0,
            'Dernière action': p.derniereAction || latest?.action || '',
            'Date dernière action': p.dateDerniereAction ? formatDate(p.dateDerniereAction, true) : (latest?.date ? formatDate(latest.date, true) : ''),
            'Résultat dernière action': resLabel,
            'Compte-rendu dernière action': derniereNote,
            'Comptes-rendus des actions (Historique complet)': tousLesComptesRendus,
            'Prochaine action': prochaineActionLabel,
            'Date prochaine action': dateProchaineActionLabel,
            'Objection / Frein': objLabel,
            'Responsable': p.responsable || '',
            'Notes prospect': p.notes || ''
        };
    });

    const ws = XLSX.utils.json_to_sheet(dataRows);

    // Largeurs de colonnes optimisées pour une lecture parfaite dans Excel
    ws['!cols'] = [
        { wch: 28 }, // Client / Établissement
        { wch: 20 }, // Type
        { wch: 15 }, // Ville
        { wch: 22 }, // Contact
        { wch: 16 }, // Téléphone
        { wch: 26 }, // Email
        { wch: 18 }, // Source / Canal
        { wch: 24 }, // Statut
        { wch: 20 }, // Niveau d'intérêt (%)
        { wch: 26 }, // Dernière action
        { wch: 20 }, // Date dernière action
        { wch: 24 }, // Résultat dernière action
        { wch: 32 }, // Compte-rendu dernière action
        { wch: 60 }, // Comptes-rendus des actions (Historique complet)
        { wch: 26 }, // Prochaine action
        { wch: 24 }, // Date prochaine action
        { wch: 26 }, // Objection / Frein
        { wch: 16 }, // Responsable
        { wch: 35 }  // Notes prospect
    ];

    // Activer l'Auto-Filtre natif Excel sur toutes les colonnes
    if (ws['!ref']) {
        ws['!autofilter'] = { ref: ws['!ref'] };
    }

    XLSX.utils.book_append_sheet(wb, ws, 'Prospects XoraLearn');

    // 2. Feuille secondaire : Historique complet de toutes les actions (avec notes & résultats)
    const historyRows = [];
    prospects.forEach(p => {
        const historyList = Array.isArray(p.historique) ? p.historique : [];
        const sorted = [...historyList].sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));

        sorted.forEach(h => {
            const resObj = h.resultat ? RESULTAT_MAP[h.resultat] : null;
            const resLabel = resObj ? `${resObj.emoji} ${resObj.label}` : (h.resultat || '');

            const objObj = h.objection ? OBJECTION_MAP[h.objection] : null;
            const objLabel = objObj ? `${objObj.emoji} ${objObj.label}` : (h.objection || '');

            const statObj = h.statut ? STATUT_MAP[h.statut] : null;
            const statLabel = statObj ? `${statObj.emoji} ${statObj.label}` : (h.statut || '');

            historyRows.push({
                'Date & Heure': h.date ? formatDateTime(h.date) : '',
                'Client / Établissement': p.etablissement || '',
                'Contact': p.contact || '',
                'Téléphone': p.telephone || '',
                'Action réalisée': h.action || '',
                'Résultat': resLabel,
                'Objection / Frein': objLabel,
                'Statut': statLabel,
                'Responsable': h.responsable || p.responsable || '',
                'Compte-rendu / Note': h.note || ''
            });
        });
    });

    const wsHistory = XLSX.utils.json_to_sheet(
        historyRows.length > 0
            ? historyRows
            : [{
                'Date & Heure': '—',
                'Client / Établissement': 'Aucune action enregistrée pour le moment',
                'Contact': '',
                'Téléphone': '',
                'Action réalisée': '',
                'Résultat': '',
                'Objection / Frein': '',
                'Statut': '',
                'Responsable': '',
                'Compte-rendu / Note': ''
            }]
    );

    wsHistory['!cols'] = [
        { wch: 18 }, // Date & Heure
        { wch: 28 }, // Client / Établissement
        { wch: 20 }, // Contact
        { wch: 16 }, // Téléphone
        { wch: 26 }, // Action réalisée
        { wch: 24 }, // Résultat
        { wch: 26 }, // Objection / Frein
        { wch: 22 }, // Statut
        { wch: 18 }, // Responsable
        { wch: 50 }  // Compte-rendu / Note
    ];

    if (wsHistory['!ref']) {
        wsHistory['!autofilter'] = { ref: wsHistory['!ref'] };
    }

    XLSX.utils.book_append_sheet(wb, wsHistory, 'Historique des actions');

    // 3. Feuille tertiaire : Guide & Méthodologie Commerciale
    const STATUT_GUIDE_MAP = {
        'a_contacter': { desc: 'Nouveau prospect importé ou identifié, aucune prise de contact réalisée.', action: 'Lancer le premier appel ou message de prospection.' },
        'contacte': { desc: 'Premier message ou appel envoyé, pas encore d\'échange constructif.', action: 'Relancer après 48h si aucune réponse.' },
        'reponse_recue': { desc: 'Le prospect a répondu (positif ou interrogation générale).', action: 'Comprendre le besoin et proposer un créneau de démonstration.' },
        'interesse': { desc: 'Le prospect montre un intérêt concret pour les solutions XoraLearn.', action: 'Fixer un créneau ferme de démo en ligne ou sur place.' },
        'demo_proposee': { desc: 'Proposition de démo transmise, créneau en attente de confirmation.', action: 'Relancer pour valider la date et l\'heure de la présentation.' },
        'demo_programmee': { desc: 'Démo validée et planifiée dans l\'agenda avec les décideurs.', action: 'Préparer la démo et envoyer un rappel courtois J-1.' },
        'demo_effectuee': { desc: 'Présentation de la solution réalisée avec succès auprès de l\'équipe.', action: 'Envoyer l\'offre commerciale chiffrée sous 24h.' },
        'offre_envoyee': { desc: 'Proposition tarifaire / devis formel transmis à l\'établissement.', action: 'Relancer sous 3 à 5 jours pour débriefing budgétaire.' },
        'negociation': { desc: 'Discussions finales sur les prix, facilités ou options du contrat.', action: 'Lever les dernières objections et valider la signature.' },
        'gagne': { desc: 'Contrat signé ou abonnement validé ! Client conquis.', action: 'Dossier clos côté prospection, passage à l\'onboarding et formation.' },
        'pas_interesse': { desc: 'Refus formulé ou inadéquation de la solution.', action: 'Renseigner obligatoirement le frein/objection puis archiver le dossier.' },
        'pas_de_reponse': { desc: 'Multiples relances infructueuses (au moins 3 tentatives sans réponse).', action: 'Mettre en veille et reprogrammer pour la rentrée suivante.' }
    };

    const RESULTAT_GUIDE_MAP = {
        'positif': { desc: 'Discussion fluide, interlocuteur à l\'écoute et réceptif.', action: 'Faire progresser le prospect vers l\'étape supérieure (démo / offre).' },
        'rdv_fixe': { desc: 'Créneau de démo ou réunion de travail formellement validé.', action: 'Passer le statut à "Démo programmée" avec date et heure précises.' },
        'devis_demande': { desc: 'L\'interlocuteur sollicite une proposition de prix détaillée.', action: 'Préparer et envoyer l\'offre chiffrée rapidement.' },
        'occupe': { desc: 'Contact joint mais indisponible sur le moment.', action: 'Reprogrammer l\'appel au créneau précis indiqué par le contact.' },
        'pas_de_reponse': { desc: 'Sonnerie dans le vide, répondeur ou message non lu.', action: 'Varier le canal (alterner appel téléphonique et WhatsApp/Email).' },
        'refus': { desc: 'L\'établissement décline la proposition ou met fin à l\'échange.', action: 'Noter le motif de refus et passer le statut à "Pas intéressé".' }
    };

    const OBJECTION_GUIDE_MAP = {
        'prix_eleve': { desc: 'Tarif jugé trop important par rapport au budget de l\'école.', action: 'Proposer un échelonnement de paiement, une formule adaptée ou démontrer le ROI.' },
        'deja_equipe': { desc: 'L\'établissement dispose déjà d\'un autre progiciel de gestion.', action: 'Mettre en avant les fonctionnalités uniques de XoraLearn (Vue360, portail parents, ergonomie).' },
        'mauvais_timing': { desc: 'Période surchargée (rentrée scolaire, examens trimestriels, vacances).', action: 'Planifier une relance ciblée sur la prochaine période plus calme.' },
        'decideur_absent': { desc: 'Le contact doit consulter le conseil d\'administration, associé ou promoteur.', action: 'Fixer une date précise pour le point de suivi avec le décideur.' },
        'prefere_papier': { desc: 'Résistance à la digitalisation, habitude des registres papier / Excel.', action: 'Proposer une démo visuelle du gain de temps concret pour la direction.' },
        'autre': { desc: 'Motif particulier non répertorié dans les freins standards.', action: 'Détailler obligatoirement la raison précise dans le compte-rendu.' }
    };

    const SOURCE_GUIDE_MAP = {
        'facebook': 'Campagnes publicitaires, page officielle ou messages Messenger Facebook.',
        'instagram': 'Messages directs (DM), stories ou publications Instagram.',
        'telephone': 'Prospection téléphonique directe (Cold calling) ou appels entrants.',
        'whatsapp': 'Échanges et relances directs via messagerie WhatsApp Business.',
        'email': 'Campagnes d\'emailing ou prise de contact directe par courriel.',
        'visite': 'Prospection physique sur le terrain, rencontre spontanée dans l\'école.',
        'recommandation': 'Recommandé par un directeur d\'école ou client existant (Bouche-à-oreille).',
        'recherche': 'Recherche Google, annuaires scolaires ou prospection ciblée.',
        'autre': 'Autre canal d\'acquisition non standard.'
    };

    const ECHEANCE_GUIDE = [
        { element: '🚨 Retard (J-X)', desc: 'La date fixée pour la prochaine action est dépassée.', action: 'Priorité absolue : à traiter dès le matin dans le filtre "À traiter".' },
        { element: '⚡ Aujourd\'hui', desc: 'Action prévue pour la date du jour.', action: 'À réaliser impérativement avant la fin de journée.' },
        { element: '📅 Cette semaine (Demain / J+X)', desc: 'Action planifiée dans les 7 prochains jours.', action: 'Préparer les supports (devis, plaquettes, liens démo) à l\'avance.' },
        { element: '⏳ En attente de retour', desc: 'Action sans date fixe car nous attendons un retour du prospect (décision, consultation).', action: 'Si aucun retour après 7 à 10 jours, reprendre contact courtoisement.' },
        { element: '⚠️ À planifier', desc: 'Prospect actif sans aucune prochaine action ni date programmée.', action: 'À compléter immédiatement pour éviter que le prospect ne soit oublié.' },
        { element: '✅ Dossier clos', desc: 'Prospect devenu client gagné.', action: 'Cycle de vente achevé avec succès.' },
        { element: '📁 Archivé', desc: 'Prospect sans suite / refusé.', action: 'Dossier archivé, plus aucune relance requise.' }
    ];

    const REGLES_GUIDE = [
        { element: 'Règle 1 : Richesse du Compte-rendu', desc: 'Chaque appel ou échange doit laisser une trace claire : interlocuteur joint, synthèse de l\'échange, objections soulevées.', action: 'Renseigner le compte-rendu immédiatement à chaud.' },
        { element: 'Règle 2 : Zéro prospect orphelin', desc: 'Chaque prospect actif doit impérativement avoir une prochaine action avec date et heure.', action: 'Ne jamais fermer un prospect sans avoir programmé la suite.' },
        { element: 'Règle 3 : Traçabilité des refus', desc: 'Tout abandon ou refus doit comporter une objection identifiée.', action: 'Permet d\'analyser les causes de perte (prix, concurrence, timing) pour adapter l\'offre.' },
        { element: 'Règle 4 : Synergie multi-canal', desc: 'Si un prospect ne répond pas au téléphone, varier avec un message WhatsApp professionnel ou un email personnalisé.', action: 'Augmente de +45% le taux de réponse.' }
    ];

    const legendData = [
        // ── 1. Architecture globale du classeur ──
        {
            'Catégorie / Contexte': '1. Architecture Fichier',
            'Élément / Colonne / Clé': 'Onglet 1 : Prospects XoraLearn',
            'Rôle dans le Système XoraLearn': 'Tableau maître : 1 ligne = 1 prospect unique avec ses coordonnées, son statut actuel et son historique compilé.',
            'Règle d\'interprétation & Analyse IA': 'Tableau principal à utiliser pour le scoring, le calcul du pipeline actif, les taux de conversion et les analyses par établissement.'
        },
        {
            'Catégorie / Contexte': '1. Architecture Fichier',
            'Élément / Colonne / Clé': 'Onglet 2 : Historique des actions',
            'Rôle dans le Système XoraLearn': 'Tableau événementiel : 1 ligne = 1 action commerciale réalisée (timeline chronologique complète de tous les échanges).',
            'Règle d\'interprétation & Analyse IA': 'Idéal pour mesurer la vélocité commerciale, le volume d\'appels/démos par agent, la productivité et les tendances temporelles.'
        },
        {
            'Catégorie / Contexte': '1. Architecture Fichier',
            'Élément / Colonne / Clé': 'Onglet 3 : Guide & Méthodologie',
            'Rôle dans le Système XoraLearn': 'Dictionnaire de données officiel, spécification des règles métier et instructions d\'analyse automatique.',
            'Règle d\'interprétation & Analyse IA': 'Sert de référence et de prompt système pour guider les IA (ChatGPT, Claude) ou analystes dans l\'exploitation correcte du fichier.'
        },

        // ── 2. Dictionnaire des colonnes de la Feuille Prospects ──
        {
            'Catégorie / Contexte': '2. Colonnes Prospects',
            'Élément / Colonne / Clé': 'Statut',
            'Rôle dans le Système XoraLearn': 'Étape actuelle du prospect dans le cycle de vente XoraLearn (12 statuts ordonnés de 0% à 100%).',
            'Règle d\'interprétation & Analyse IA': 'Statuts clos : "Client gagné" (conversion réussie) et "Pas intéressé" (refus). Tout autre statut = dossier en cours (Pipeline Actif).'
        },
        {
            'Catégorie / Contexte': '2. Colonnes Prospects',
            'Élément / Colonne / Clé': 'Niveau d\'intérêt (%)',
            'Rôle dans le Système XoraLearn': 'Indicateur de maturité et probabilité d\'achat (0% = inactif, 50% = intéressé, 70% = démo, 100% = gagné).',
            'Règle d\'interprétation & Analyse IA': 'Permet de prioriser les relances à fort potentiel (>60%) et de pondérer la valeur prévisionnelle du pipeline de vente.'
        },
        {
            'Catégorie / Contexte': '2. Colonnes Prospects',
            'Élément / Colonne / Clé': 'Dernière action & Date',
            'Rôle dans le Système XoraLearn': 'Intitulé et date de la plus récente interaction réalisée avec ce prospect.',
            'Règle d\'interprétation & Analyse IA': 'Mesurer le temps écoulé depuis la dernière interaction pour détecter les comptes délaissés.'
        },
        {
            'Catégorie / Contexte': '2. Colonnes Prospects',
            'Élément / Colonne / Clé': 'Résultat dernière action',
            'Rôle dans le Système XoraLearn': 'Issue directe de la dernière action (ex: Échange positif, Ne répond pas, RDV fixé, Refus).',
            'Règle d\'interprétation & Analyse IA': 'Permet d\'évaluer immédiatement la tendance du dossier (favorable vs bloqué).'
        },
        {
            'Catégorie / Contexte': '2. Colonnes Prospects',
            'Élément / Colonne / Clé': 'Compte-rendu dernière action',
            'Rôle dans le Système XoraLearn': 'Note textuelle saisie par le commercial lors de sa toute dernière action.',
            'Règle d\'interprétation & Analyse IA': 'Résumé express de la dernière conversation utile pour une relance rapide sans lire tout l\'historique.'
        },
        {
            'Catégorie / Contexte': '2. Colonnes Prospects',
            'Élément / Colonne / Clé': 'Comptes-rendus des actions (Historique complet)',
            'Rôle dans le Système XoraLearn': 'Timeline complète compilée dans la cellule : chaque action sur une ligne avec date, issue, frein, agent et note.',
            'Règle d\'interprétation & Analyse IA': 'Format : • Date · Action (Résultat · Frein · [Agent]) : Note. Idéal pour l\'analyse sémantique, la synthèse IA et le profilage client.'
        },
        {
            'Catégorie / Contexte': '2. Colonnes Prospects',
            'Élément / Colonne / Clé': 'Prochaine action',
            'Rôle dans le Système XoraLearn': 'Tâche commerciale à accomplir, ou statut intelligent de clôture.',
            'Règle d\'interprétation & Analyse IA': 'Prend "Client converti" si gagné, ou "Sans suite (Refus)" si pas intéressé. Sinon affiche l\'action programmée.'
        },
        {
            'Catégorie / Contexte': '2. Colonnes Prospects',
            'Élément / Colonne / Clé': 'Date prochaine action',
            'Rôle dans le Système XoraLearn': 'Date et heure de la relance, ou message d\'état intelligent selon la situation du prospect.',
            'Règle d\'interprétation & Analyse IA': '"Dossier clos" (gagné), "Archivé" (refus), "En attente de retour" (attente réponse), "À planifier" (date manquante), ou "JJ/MM/AAAA à HH:MM".'
        },
        {
            'Catégorie / Contexte': '2. Colonnes Prospects',
            'Élément / Colonne / Clé': 'Objection / Frein',
            'Rôle dans le Système XoraLearn': 'Obstacle majeur à la conversion identifié par le commercial (Prix, Concurrent, Timing, Décideur, etc.).',
            'Règle d\'interprétation & Analyse IA': 'Colonne stratégique : agréger pour trouver les causes dominantes d\'échec ou de ralentissement des ventes.'
        },
        {
            'Catégorie / Contexte': '2. Colonnes Prospects',
            'Élément / Colonne / Clé': 'Notes prospect',
            'Rôle dans le Système XoraLearn': 'Commentaires généraux et informations permanentes sur l\'établissement (taille, besoins spécifiques, équipement).',
            'Règle d\'interprétation & Analyse IA': 'Contexte statique de la fiche client, distinct des comptes-rendus d\'actions qui sont événementiels.'
        },

        // ── 3. Interprétation des Messages Intelligents & Échéances ──
        {
            'Catégorie / Contexte': '3. Messages Intelligents',
            'Élément / Colonne / Clé': '⏳ En attente de retour',
            'Rôle dans le Système XoraLearn': 'Action de temporisation (ex: "Attendre réponse", "En réflexion") sans date butoir imposée.',
            'Règle d\'interprétation & Analyse IA': 'Situation normale : le client étudie l\'offre. Ne pas classer en anomalie ni en retard. Relancer après 7-10j.'
        },
        {
            'Catégorie / Contexte': '3. Messages Intelligents',
            'Élément / Colonne / Clé': '⚠️ À planifier',
            'Rôle dans le Système XoraLearn': 'Prospect actif n\'ayant aucune date de prochaine action programmée.',
            'Règle d\'interprétation & Analyse IA': 'Anomalie de gestion commerciale : dossier sans suite planifiée risquant d\'être oublié.'
        },
        {
            'Catégorie / Contexte': '3. Messages Intelligents',
            'Élément / Colonne / Clé': '✅ Client converti / Dossier clos',
            'Rôle dans le Système XoraLearn': 'Statut "Client gagné" : le prospect a signé ou souscrit à XoraLearn.',
            'Règle d\'interprétation & Analyse IA': 'Succès commercial. Dossier clos, aucune relance requise.'
        },
        {
            'Catégorie / Contexte': '3. Messages Intelligents',
            'Élément / Colonne / Clé': '❌ Sans suite (Refus) / Archivé',
            'Rôle dans le Système XoraLearn': 'Statut "Pas intéressé" : refus formulé par l\'établissement.',
            'Règle d\'interprétation & Analyse IA': 'Échec commercial. Analyser systématiquement la colonne "Objection / Frein" associée.'
        },

        // ── 4. Statuts du Pipeline & Progression ──
        ...STATUTS.map(s => ({
            'Catégorie / Contexte': '4. Statuts du Pipeline',
            'Élément / Colonne / Clé': `${s.emoji} ${s.label} (${s.value})`,
            'Rôle dans le Système XoraLearn': `Palier dans le cycle de vente XoraLearn (Intérêt conseillé : ${s.interet}%).`,
            'Règle d\'interprétation & Analyse IA': `${STATUT_GUIDE_MAP[s.value]?.desc || s.label} | Action recommandée : ${STATUT_GUIDE_MAP[s.value]?.action || 'Suivi'}`
        })),

        // ── 5. Résultats d'Actions ──
        ...RESULTATS_ACTION.map(r => ({
            'Catégorie / Contexte': '5. Résultats d\'Actions',
            'Élément / Colonne / Clé': `${r.emoji} ${r.label} (${r.value})`,
            'Rôle dans le Système XoraLearn': 'Issue immédiate constatée lors d\'une action commerciale (appel, démo, message).',
            'Règle d\'interprétation & Analyse IA': `${RESULTAT_GUIDE_MAP[r.value]?.desc || r.label} | Impact système : ${RESULTAT_GUIDE_MAP[r.value]?.action || 'Suivi'}`
        })),

        // ── 6. Objections & Freins Commerciaux ──
        ...OBJECTIONS.map(o => ({
            'Catégorie / Contexte': '6. Objections & Freins',
            'Élément / Colonne / Clé': `${o.emoji} ${o.label} (${o.value})`,
            'Rôle dans le Système XoraLearn': 'Obstacle exprimé par le client empêchant la progression vers la vente.',
            'Règle d\'interprétation & Analyse IA': `${OBJECTION_GUIDE_MAP[o.value]?.desc || o.label} | Stratégie recommandée : ${OBJECTION_GUIDE_MAP[o.value]?.action || 'Traitement'}`
        })),

        // ── 7. Canaux d'Acquisition (Sources) ──
        ...SOURCES.map(s => ({
            'Catégorie / Contexte': '7. Canaux d\'Acquisition',
            'Élément / Colonne / Clé': `${s.label} (${s.value})`,
            'Rôle dans le Système XoraLearn': 'Origine du prospect / canal par lequel il est entré dans le système.',
            'Règle d\'interprétation & Analyse IA': `${SOURCE_GUIDE_MAP[s.value] || s.label} | Analyser le taux de conversion par canal pour optimiser le budget marketing.`
        })),

        // ── 8. Formules & Instructions pour l'Analyse IA ──
        {
            'Catégorie / Contexte': '8. Formules & Analyse IA',
            'Élément / Colonne / Clé': 'Pipeline Actif (En cours)',
            'Rôle dans le Système XoraLearn': 'Ensemble des prospects actifs faisant l\'objet d\'un suivi commercial.',
            'Règle d\'interprétation & Analyse IA': 'Formule : COUNT(prospects WHERE Statut NOT IN ("Client gagné", "Pas intéressé")).'
        },
        {
            'Catégorie / Contexte': '8. Formules & Analyse IA',
            'Élément / Colonne / Clé': 'Taux de Conversion Global (%)',
            'Rôle dans le Système XoraLearn': 'Ratio d\'efficacité globale de la prospection commerciale.',
            'Règle d\'interprétation & Analyse IA': 'Formule : (COUNT(Statut == "Client gagné") / Total Prospects) * 100.'
        },
        {
            'Catégorie / Contexte': '8. Formules & Analyse IA',
            'Élément / Colonne / Clé': 'Taux de Dossiers à Risque / Retard',
            'Rôle dans le Système XoraLearn': 'Mesure de la réactivité et des opportunités en souffrance.',
            'Règle d\'interprétation & Analyse IA': 'Formule : COUNT(prospects actifs WHERE Date prochaine action < Aujourd\'hui) / Pipeline Actif * 100.'
        },
        {
            'Catégorie / Contexte': '8. Formules & Analyse IA',
            'Élément / Colonne / Clé': 'Analyse des Goulots d\'étranglement',
            'Rôle dans le Système XoraLearn': 'Identifier à quelle étape du pipeline les prospects s\'accumulent sans avancer.',
            'Règle d\'interprétation & Analyse IA': 'Calculer la distribution par Statut. Ex : beaucoup de "Démo proposée" sans "Démo programmée" = difficulté de calage de RDV.'
        },
        {
            'Catégorie / Contexte': '8. Formules & Analyse IA',
            'Élément / Colonne / Clé': 'Analyse des Motifs de Rejet (Top Objections)',
            'Rôle dans le Système XoraLearn': 'Comprendre pourquoi les ventes échouent pour adapter le produit ou la tarification.',
            'Règle d\'interprétation & Analyse IA': 'GROUP BY "Objection / Frein" sur les statuts "Pas intéressé" ou intérêt faible. Comparer prix vs concurrents vs timing.'
        },
        {
            'Catégorie / Contexte': '8. Formules & Analyse IA',
            'Élément / Colonne / Clé': 'Analyse Sémantique des Comptes-rendus',
            'Rôle dans le Système XoraLearn': 'Extraction des besoins, des décideurs clés et du sentiment client dans les textes.',
            'Règle d\'interprétation & Analyse IA': 'Analyser la colonne "Comptes-rendus des actions" : extraire les fonctionnalités les plus demandées et les questions récurrentes.'
        },

        // ── 9. Règles fondamentales XoraLearn ──
        ...REGLES_GUIDE.map(r => ({
            'Catégorie / Contexte': '9. Règles d\'or XoraLearn',
            'Élément / Colonne / Clé': r.element,
            'Rôle dans le Système XoraLearn': r.desc,
            'Règle d\'interprétation & Analyse IA': r.action
        }))
    ];

    const wsLegend = XLSX.utils.json_to_sheet(legendData);
    wsLegend['!cols'] = [
        { wch: 28 }, // Catégorie / Contexte
        { wch: 36 }, // Élément / Colonne / Clé
        { wch: 65 }, // Rôle dans le Système XoraLearn
        { wch: 75 }  // Règle d'interprétation & Analyse IA
    ];
    if (wsLegend['!ref']) {
        wsLegend['!autofilter'] = { ref: wsLegend['!ref'] };
    }
    XLSX.utils.book_append_sheet(wb, wsLegend, 'Guide & Méthodologie');

    // Déclencher le téléchargement
    const name = fileName || `prospects-xoralearn-${new Date().toISOString().slice(0, 10)}.xlsx`;
    XLSX.writeFile(wb, name, { bookType: 'xlsx' });
};

// ── Télécharger un modèle Excel vierge pour l'import ──

export const downloadProspectsTemplate = () => {
    const wb = XLSX.utils.book_new();

    const templateRows = [
        {
            'Client / Établissement': 'École Les Génies (Exemple)',
            'Type d\'établissement': 'École primaire privée',
            'Ville': 'Tunis',
            'Contact': 'Directeur Général',
            'Téléphone': '98 123 456',
            'Email': 'direction@lesgenies.tn',
            'Source': 'Facebook',
            'Statut': 'Contacté',
            'Niveau d\'intérêt (%)': 30,
            'Dernière action': 'Email de présentation envoyé',
            'Date dernière action': '2026-10-07',
            'Prochaine action': 'Relancer par téléphone',
            'Date prochaine action': '2026-10-10',
            'Responsable': 'Brahim',
            'Notes': 'Intéressé par le module Vue360 et mobile'
        },
        {
            'Client / Établissement': 'Centre Excellence (Exemple)',
            'Type d\'établissement': 'Centre de formation',
            'Ville': 'Sousse',
            'Contact': 'Mme Amel',
            'Téléphone': '22 456 789',
            'Email': 'contact@excellence.tn',
            'Source': 'WhatsApp',
            'Statut': 'Démo programmée',
            'Niveau d\'intérêt (%)': 70,
            'Dernière action': 'Démo fixée en ligne',
            'Date dernière action': '2026-10-06',
            'Prochaine action': 'Faire démo Meet',
            'Date prochaine action': '2026-10-12',
            'Responsable': 'Brahim',
            'Notes': 'Veut voir transport et facturation'
        }
    ];

    const ws = XLSX.utils.json_to_sheet(templateRows);
    ws['!cols'] = [
        { wch: 28 }, { wch: 20 }, { wch: 15 }, { wch: 22 },
        { wch: 16 }, { wch: 26 }, { wch: 16 }, { wch: 22 },
        { wch: 20 }, { wch: 26 }, { wch: 20 }, { wch: 26 },
        { wch: 20 }, { wch: 16 }, { wch: 35 }
    ];

    XLSX.utils.book_append_sheet(wb, ws, 'Modèle Import');
    XLSX.writeFile(wb, 'modele-import-prospects-xoralearn.xlsx', { bookType: 'xlsx' });
};

// ── Normalisation intelligente pour l'import ──

const normalizeStatut = (raw) => {
    if (!raw) return 'a_contacter';
    const s = String(raw).toLowerCase().trim();

    if (s.includes('gagn') || s.includes('client gagn') || s.includes('won') || s.includes('signé')) return 'gagne';
    if (s.includes('pas intér') || s.includes('pas inter') || s.includes('perdu') || s.includes('refus')) return 'pas_interesse';
    if (s.includes('pas de rép') || s.includes('pas de rep') || s.includes('sans reponse')) return 'pas_de_reponse';
    if (s.includes('négo') || s.includes('nego')) return 'negociation';
    if (s.includes('offre') || s.includes('devis')) return 'offre_envoyee';
    if (s.includes('effectu') || s.includes('faite')) return 'demo_effectuee';
    if (s.includes('programm') || s.includes('fixée') || s.includes('fixee') || s.includes('prévue') || s.includes('prevue')) return 'demo_programmee';
    if (s.includes('propos')) return 'demo_proposee';
    if (s.includes('intér') || s.includes('inter')) return 'interesse';
    if (s.includes('réponse') || s.includes('reponse')) return 'reponse_recue';
    if (s.includes('contact')) return 'contacte';
    if (s.includes('à contacter') || s.includes('a contacter') || s.includes('nouveau')) return 'a_contacter';

    // Recherche directe par value
    const direct = STATUTS.find(x => x.value === s);
    return direct ? direct.value : 'a_contacter';
};

const normalizeSource = (raw) => {
    if (!raw) return 'recherche';
    const s = String(raw).toLowerCase().trim();

    if (s.includes('fb') || s.includes('facebook')) return 'facebook';
    if (s.includes('insta')) return 'instagram';
    if (s.includes('tel') || s.includes('tél') || s.includes('phone') || s.includes('appel')) return 'telephone';
    if (s.includes('whats') || s.includes('wa')) return 'whatsapp';
    if (s.includes('mail')) return 'email';
    if (s.includes('visit') || s.includes('terrain') || s.includes('sur place')) return 'visite';
    if (s.includes('recom') || s.includes('bouche')) return 'recommandation';
    if (s.includes('rech') || s.includes('google') || s.includes('web')) return 'recherche';

    const direct = SOURCES.find(x => x.value === s);
    return direct ? direct.value : 'autre';
};

const normalizeObjection = (raw) => {
    if (!raw) return '';
    const s = String(raw).toLowerCase().trim();
    if (s.includes('prix') || s.includes('budget') || s.includes('cher')) return 'prix_eleve';
    if (s.includes('équip') || s.includes('equip') || s.includes('concurrent')) return 'deja_equipe';
    if (s.includes('timing') || s.includes('rentrée') || s.includes('rentree') || s.includes('examen')) return 'mauvais_timing';
    if (s.includes('décideur') || s.includes('decideur') || s.includes('absent') || s.includes('réflexion') || s.includes('reflexion')) return 'decideur_absent';
    if (s.includes('papier') || s.includes('excel')) return 'prefere_papier';

    const direct = OBJECTIONS.find(x => x.value === s);
    return direct ? direct.value : (s ? 'autre' : '');
};

const parseExcelDate = (raw) => {
    if (!raw) return null;
    if (raw instanceof Date && !isNaN(raw.getTime())) return toInputDate(raw);

    // Si c'est un nombre serial Excel (ex: 45572)
    if (typeof raw === 'number') {
        const dateObj = new Date(Math.round((raw - 25569) * 86400 * 1000));
        if (!isNaN(dateObj.getTime())) return toInputDate(dateObj);
    }

    const str = String(raw).trim();
    if (!str) return null;

    // Format JJ/MM/AAAA ou JJ-MM-AAAA
    const matchFR = str.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})$/);
    if (matchFR) {
        const day = matchFR[1].padStart(2, '0');
        const month = matchFR[2].padStart(2, '0');
        let year = matchFR[3];
        if (year.length === 2) year = '20' + year;
        return `${year}-${month}-${day}`;
    }

    // Format AAAA-MM-JJ
    const matchISO = str.match(/^(\d{4})[/-](\d{1,2})[/-](\d{1,2})$/);
    if (matchISO) {
        const year = matchISO[1];
        const month = matchISO[2].padStart(2, '0');
        const day = matchISO[3].padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    const parsed = new Date(str);
    if (!isNaN(parsed.getTime())) return toInputDate(parsed);
    return null;
};

// ── Lecture et parsing du fichier Excel ou CSV ──

export const parseProspectsFile = async (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = (e) => {
            try {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: 'array', cellDates: true });

                const firstSheetName = workbook.SheetNames[0];
                if (!firstSheetName) {
                    throw new Error('Le fichier ne contient aucune feuille.');
                }

                const worksheet = workbook.Sheets[firstSheetName];
                const rawRows = XLSX.utils.sheet_to_json(worksheet, { defval: '' });

                if (!rawRows || rawRows.length === 0) {
                    throw new Error('Le tableau Excel est vide.');
                }

                const parsedList = [];
                const errors = [];

                rawRows.forEach((row, idx) => {
                    // Trouver la clé correspondant à chaque champ de manière flexible
                    const keys = Object.keys(row);
                    const findVal = (regex) => {
                        const matchedKey = keys.find(k => regex.test(k.trim().toLowerCase()));
                        return matchedKey ? row[matchedKey] : '';
                    };

                    const etablissement = (
                        findVal(/^(client|établissement|etablissement|nom|ecole|école|school|organization)$/i) ||
                        findVal(/(client|établissement|etablissement)/i) ||
                        row['Client / Établissement'] ||
                        row['Établissement'] ||
                        row['Nom'] ||
                        ''
                    ).toString().trim();

                    if (!etablissement) {
                        errors.push(`Ligne ${idx + 2} ignorée : Nom de l'établissement manquant.`);
                        return;
                    }

                    const typeEtablissement = (
                        findVal(/type/i) ||
                        row['Type d\'établissement'] ||
                        ''
                    ).toString().trim();

                    const ville = (findVal(/ville|city|gouvernorat/i) || '').toString().trim();
                    const contact = (findVal(/contact|responsable|personne|directeur/i) || '').toString().trim();
                    const telephone = (findVal(/téléphone|telephone|tel|phone|mobile|portable/i) || '').toString().trim();
                    const email = (findVal(/email|mail|courriel/i) || '').toString().trim();

                    const rawSource = findVal(/source|canal/i);
                    const source = normalizeSource(rawSource);

                    const rawStatut = findVal(/statut|status|etat|état/i);
                    const statut = normalizeStatut(rawStatut);

                    const rawInteret = findVal(/intérêt|interet|%|niveau/i);
                    let interet = Number(rawInteret);
                    if (isNaN(interet) || interet < 0 || interet > 100) {
                        // Valeur par défaut liée au statut
                        interet = STATUT_MAP[statut]?.interet ?? 0;
                    }

                    const derniereAction = (findVal(/dernière action|derniere action|action|action précédente/i) || '').toString().trim();
                    const rawDateDerniere = findVal(/date dernière|date derniere/i);
                    const dateDerniereAction = parseExcelDate(rawDateDerniere);

                    let prochaineAction = (findVal(/prochaine action|action suivante|a faire|à faire/i) || '').toString().trim();
                    const rawDateProchaine = findVal(/date prochaine|date relance|date|échéance|echeance/i);
                    let dateProchaineAction = parseExcelDate(rawDateProchaine);

                    // Si statut fermé (gagné ou pas intéressé), réinitialiser la prochaine action
                    if (statut === 'gagne' || statut === 'pas_interesse') {
                        prochaineAction = '';
                        dateProchaineAction = null;
                    }

                    const rawObjection = findVal(/objection|frein|obstacle/i);
                    const objection = normalizeObjection(rawObjection);

                    const responsable = (findVal(/responsable|commercial|agent|assigné/i) || '').toString().trim();
                    const notes = (findVal(/notes|note|remarque|commentaire|besoin/i) || '').toString().trim();

                    parsedList.push({
                        etablissement,
                        typeEtablissement,
                        ville,
                        contact,
                        telephone,
                        email,
                        source,
                        statut,
                        interet,
                        derniereAction,
                        dateDerniereAction,
                        prochaineAction,
                        dateProchaineAction,
                        responsable,
                        objection,
                        notes
                    });
                });

                resolve({
                    prospects: parsedList,
                    totalParsed: parsedList.length,
                    errors
                });
            } catch (err) {
                reject(err);
            }
        };

        reader.onerror = () => reject(new Error('Erreur de lecture du fichier.'));
        reader.readAsArrayBuffer(file);
    });
};
